import { useState, type KeyboardEvent } from "react";
import { CMMD_PRIORITY_ORDER, type CmmdMember } from "@cmmd-forge/contracts";

import { PersonAvatar } from "~/components/identity/PersonAvatar";
import { cn } from "~/lib/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { PriorityIcon } from "./ProjectsPriorityIcon";
import {
  PROJECT_SETTINGS_NONE_VALUE,
  resolveSettingsSelection,
  resolveStatusSelection,
  type CmmdProjectSettingsPatch,
} from "./projectSettingsPatch";
import {
  PROJECT_STATUS_OPTIONS,
  projectPriorityLabel,
  projectStatusLabel,
  projectStatusTone,
  semanticDotClasses,
} from "./projectsModel";

/** Every control writes through one saver, so the tab owns error + pending state once. */
export type ProjectSettingsSaver = (patch: CmmdProjectSettingsPatch) => void;

const CONTROL_WIDTH = "w-full sm:w-64";
const NONE_VALUE = PROJECT_SETTINGS_NONE_VALUE;
/**
 * Archiving is a Danger-zone action with a confirmation. Leaving it in the
 * status picker made it a one-click, unconfirmed path to the same outcome.
 * An already-archived project still reads correctly: the trigger renders from
 * the current value, not from this list.
 */
const STATUS_PICKER_OPTIONS = PROJECT_STATUS_OPTIONS.filter(
  (option) => option.value !== "archived",
);
export function ProjectNameControl(props: {
  readonly title: string;
  readonly disabled: boolean;
  readonly onSave: ProjectSettingsSaver;
}) {
  // `baseline` is the title as it stood when the field took focus. Committing
  // on `draft !== props.title` alone would push a stale draft back over a title
  // that changed underneath an untouched focused field.
  const [edit, setEdit] = useState<{ readonly baseline: string; readonly draft: string } | null>(
    null,
  );
  const value = edit === null ? props.title : edit.draft;

  const commit = () => {
    const pending = edit;
    setEdit(null);
    if (pending === null || pending.draft === pending.baseline) return;
    const next = pending.draft.trim();
    if (next.length === 0 || next === props.title) return;
    props.onSave({ title: next });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.currentTarget.blur();
    } else if (event.key === "Escape") {
      event.preventDefault();
      // Stay in edit mode: dropping out while the input still holds focus pins
      // the display to the saved title and looks frozen.
      setEdit({ baseline: props.title, draft: props.title });
    }
  };

  return (
    <Input
      aria-label="Project board name"
      className={CONTROL_WIDTH}
      value={value}
      disabled={props.disabled}
      onFocus={() => setEdit({ baseline: props.title, draft: props.title })}
      onChange={(event) => {
        const draft = event.currentTarget.value;
        setEdit((current) => ({ baseline: current?.baseline ?? props.title, draft }));
      }}
      onBlur={commit}
      onKeyDown={handleKeyDown}
    />
  );
}

/**
 * The project's name inside Forge.
 *
 * Empty means "no separate name" — the field falls back to the board title, and
 * shows it as the placeholder so an empty box still tells the reader what
 * actually renders. Committing an empty field saves `null` rather than `""`,
 * because a stored empty string would render a nameless sidebar row.
 *
 * Kept as its own control rather than a mode on `ProjectNameControl`: the two
 * differ in exactly the places that matter (empty is legal here and clears;
 * empty is a no-op there, since the board name cannot be blank), and folding
 * them together is how one of those rules ends up applied to both fields.
 */
export function ProjectDisplayNameControl(props: {
  readonly shortName: string | null;
  readonly title: string;
  readonly disabled: boolean;
  readonly onSave: ProjectSettingsSaver;
}) {
  const saved = props.shortName ?? "";
  // Same baseline discipline as the board name: committing on `draft !== saved`
  // alone would push a stale draft over a value that changed underneath an
  // untouched focused field.
  const [edit, setEdit] = useState<{ readonly baseline: string; readonly draft: string } | null>(
    null,
  );
  const value = edit === null ? saved : edit.draft;

  const commit = () => {
    const pending = edit;
    setEdit(null);
    if (pending === null || pending.draft === pending.baseline) return;
    const next = pending.draft.trim();
    if (next === saved) return;
    props.onSave({ shortName: next.length === 0 ? null : next });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.currentTarget.blur();
    } else if (event.key === "Escape") {
      event.preventDefault();
      setEdit({ baseline: saved, draft: saved });
    }
  };

  return (
    <Input
      aria-label="Project name in Forge"
      className={CONTROL_WIDTH}
      value={value}
      placeholder={props.title}
      disabled={props.disabled}
      onFocus={() => setEdit({ baseline: saved, draft: saved })}
      onChange={(event) => {
        const draft = event.currentTarget.value;
        setEdit((current) => ({ baseline: current?.baseline ?? saved, draft }));
      }}
      onBlur={commit}
      onKeyDown={handleKeyDown}
    />
  );
}

/**
 * The project's emoji.
 *
 * Only the emoji is settable here. CMMD owns `iconUrl` — an S3 key its
 * repository icon sync writes after fetching and normalising an image from the
 * linked repo — so a person picks the emoji, and choosing one clears the synced
 * image server-side and stops the sync overwriting the choice. Emptying the
 * field hands the identity back to the repository.
 */
export function ProjectIconControl(props: {
  readonly icon: string | null;
  readonly disabled: boolean;
  readonly onSave: ProjectSettingsSaver;
}) {
  const saved = props.icon ?? "";
  // Same baseline/draft shape as the name field, for the same reason: a stale
  // draft must not be pushed over an icon that changed under a focused field.
  const [edit, setEdit] = useState<{ readonly baseline: string; readonly draft: string } | null>(
    null,
  );
  const value = edit === null ? saved : edit.draft;

  const commit = () => {
    const pending = edit;
    setEdit(null);
    if (pending === null || pending.draft === pending.baseline) return;
    const next = pending.draft.trim();
    if (next === saved) return;
    // Empty is a clear, not a no-op: it is how a person gives the identity back
    // to the repository sync.
    props.onSave({ icon: next === "" ? null : next });
  };

  return (
    <Input
      aria-label="Project icon"
      // A single emoji, not prose. The server bounds it at 16 characters
      // because one emoji can be several code points (ZWJ, skin tone).
      maxLength={16}
      placeholder="Emoji"
      className="w-20 text-center"
      value={value}
      disabled={props.disabled}
      onFocus={() => setEdit({ baseline: saved, draft: saved })}
      onChange={(event) => {
        const draft = event.currentTarget.value;
        setEdit((current) => ({ baseline: current?.baseline ?? saved, draft }));
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.currentTarget.blur();
        } else if (event.key === "Escape") {
          event.preventDefault();
          setEdit({ baseline: saved, draft: saved });
        }
      }}
    />
  );
}

export function ProjectStatusControl(props: {
  readonly status: string | null;
  readonly disabled: boolean;
  readonly onSave: ProjectSettingsSaver;
}) {
  const value = props.status ?? "not_started";
  return (
    <Select
      value={value}
      disabled={props.disabled}
      onValueChange={(next) => {
        // Compare against the stored status, not the "Not started" fallback:
        // a statusless project must still be settable to `not_started`.
        const patch = resolveStatusSelection(props.status ?? "", next);
        if (patch !== null) props.onSave(patch);
      }}
    >
      <SelectTrigger className={CONTROL_WIDTH} aria-label="Project status">
        <SelectValue>
          <span className="inline-flex min-w-0 items-center gap-2">
            <span
              className={cn(
                "size-1.5 shrink-0 rounded-full",
                semanticDotClasses(projectStatusTone(value)),
              )}
              aria-hidden
            />
            <span className="truncate">{projectStatusLabel(value)}</span>
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectPopup align="end" alignItemWithTrigger={false}>
        {STATUS_PICKER_OPTIONS.map((option) => (
          <SelectItem key={option.value} hideIndicator value={option.value}>
            <span className="inline-flex min-w-0 items-center gap-2">
              <span
                className={cn(
                  "size-1.5 shrink-0 rounded-full",
                  semanticDotClasses(projectStatusTone(option.value)),
                )}
                aria-hidden
              />
              <span className="truncate">{option.label}</span>
            </span>
          </SelectItem>
        ))}
      </SelectPopup>
    </Select>
  );
}

export function ProjectPriorityControl(props: {
  readonly priority: string | null | undefined;
  readonly disabled: boolean;
  readonly onSave: ProjectSettingsSaver;
}) {
  const value = props.priority ?? NONE_VALUE;
  return (
    <Select
      value={value}
      disabled={props.disabled}
      onValueChange={(next) => {
        const patch = resolveSettingsSelection("priority", value, next);
        if (patch !== null) props.onSave(patch);
      }}
    >
      <SelectTrigger className={CONTROL_WIDTH} aria-label="Project priority">
        <SelectValue>
          <span className="inline-flex min-w-0 items-center gap-2">
            <PriorityIcon priority={props.priority ?? null} />
            <span className="truncate">
              {props.priority ? projectPriorityLabel(props.priority) : "No priority"}
            </span>
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectPopup align="end" alignItemWithTrigger={false}>
        <SelectItem hideIndicator value={NONE_VALUE}>
          No priority
        </SelectItem>
        {CMMD_PRIORITY_ORDER.map((priority) => (
          <SelectItem key={priority} hideIndicator value={priority}>
            <span className="inline-flex min-w-0 items-center gap-2">
              <PriorityIcon priority={priority} />
              <span className="truncate">{projectPriorityLabel(priority)}</span>
            </span>
          </SelectItem>
        ))}
      </SelectPopup>
    </Select>
  );
}

export function ProjectLeadControl(props: {
  readonly ownerId: string | null | undefined;
  readonly ownerName: string | null | undefined;
  readonly ownerAvatarUrl: string | null;
  readonly members: readonly CmmdMember[];
  readonly disabled: boolean;
  readonly onSave: ProjectSettingsSaver;
}) {
  const ownerId = props.ownerId != null ? String(props.ownerId) : null;
  // A lead who left the project still owns it in CMMD; keep them selectable
  // rather than silently resetting the control to "Unassigned".
  const knownOwner = ownerId !== null && props.members.some((member) => member.id === ownerId);
  const value = ownerId ?? NONE_VALUE;

  return (
    <Select
      value={value}
      disabled={props.disabled}
      onValueChange={(next) => {
        const patch = resolveSettingsSelection("ownerId", value, next);
        if (patch !== null) props.onSave(patch);
      }}
    >
      <SelectTrigger className={CONTROL_WIDTH} aria-label="Project lead">
        <SelectValue>
          <span className="inline-flex min-w-0 items-center gap-2">
            {props.ownerName ? (
              <PersonAvatar
                name={props.ownerName}
                avatarUrl={props.ownerAvatarUrl}
                className="flex size-4 shrink-0 items-center justify-center rounded-full bg-muted text-[8px] font-medium text-muted-foreground"
              />
            ) : null}
            <span className="truncate">{props.ownerName ?? "Unassigned"}</span>
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectPopup align="end" alignItemWithTrigger={false}>
        <SelectItem hideIndicator value={NONE_VALUE}>
          Unassigned
        </SelectItem>
        {!knownOwner && ownerId !== null ? (
          <SelectItem hideIndicator value={ownerId}>
            {props.ownerName ?? "Current lead"}
          </SelectItem>
        ) : null}
        {props.members.map((member) => (
          <SelectItem key={member.id} hideIndicator value={member.id}>
            <span className="inline-flex min-w-0 items-center gap-2">
              <PersonAvatar
                name={member.name}
                avatarUrl={member.avatarUrl}
                className="flex size-4 shrink-0 items-center justify-center rounded-full bg-muted text-[8px] font-medium text-muted-foreground"
              />
              <span className="truncate">{member.name}</span>
            </span>
          </SelectItem>
        ))}
      </SelectPopup>
    </Select>
  );
}

/** CMMD stores dates as ISO strings; `<input type="date">` wants `YYYY-MM-DD`. */
function dateInputValue(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : "";
}

export function ProjectTargetDateControl(props: {
  readonly targetEndDate: string | null | undefined;
  readonly disabled: boolean;
  readonly onSave: ProjectSettingsSaver;
}) {
  const saved = dateInputValue(props.targetEndDate);
  const [draft, setDraft] = useState<string | null>(null);

  // A native date input fires `change` with an empty value while a segment is
  // still being edited. Saving that would clear the stored date, and the
  // in-flight save then disables the field and takes focus with it, so the edit
  // could not be finished. Commit on blur, the same as the name field.
  const commit = () => {
    const pending = draft;
    setDraft(null);
    if (pending === null || pending === saved) return;
    props.onSave({ targetEndDate: pending.length > 0 ? pending : null });
  };

  return (
    <Input
      type="date"
      aria-label="Target date"
      className={CONTROL_WIDTH}
      value={draft ?? saved}
      disabled={props.disabled}
      onChange={(event) => setDraft(event.currentTarget.value)}
      onBlur={commit}
    />
  );
}

export function ProjectMembersControl(props: {
  readonly members: readonly CmmdMember[];
  readonly onManageMembers: () => void;
}) {
  const shown = props.members.slice(0, 5);
  const overflow = props.members.length - shown.length;
  return (
    <div className="flex items-center gap-2">
      {shown.length > 0 ? (
        <div className="flex items-center -space-x-1.5">
          {shown.map((member) => (
            <PersonAvatar
              key={member.id}
              name={member.name}
              avatarUrl={member.avatarUrl}
              className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-medium text-muted-foreground ring-2 ring-background"
            />
          ))}
        </div>
      ) : null}
      {overflow > 0 ? (
        <span className="text-xs tabular-nums text-muted-foreground">+{overflow}</span>
      ) : null}
      <Button variant="outline" size="sm" onClick={props.onManageMembers}>
        Manage team
      </Button>
    </div>
  );
}
